import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Inbox, Megaphone, Users, Ban, Reply, Bell, MessageSquareText, Tags } from "lucide-react";
import { GuestRepliesPanel } from "./GuestRepliesPanel";
import { MailLabels } from "./MailLabels";
import { mailApi, setGmailAccount } from "./mailApi";
import { MailSenders } from "./MailSenders";
import { MailNeedsReply } from "./MailNeedsReply";
import { MailRules } from "./MailRules";

const TABS = [
  ["guests", "Risposte ospiti", MessageSquareText], ["reply", "Da rispondere", Reply], ["bulk", "Pubblicità e newsletter", Megaphone], ["auto", "Notifiche automatiche", Bell],
  ["people", "Persone e fornitori", Users], ["all", "Tutti i mittenti", Inbox], ["labels", "Etichette", Tags], ["rules", "Bloccati", Ban],
];

// Admin → Impostazioni → Posta: triage of the main Gmail inbox.
export function MailCleanupPanel() {
  const [st, setSt] = useState(null);
  const [tab, setTab] = useState("guests");
  const [tick, setTick] = useState(0);

  const refresh = () => mailApi.status().then((d) => { setGmailAccount(d.mailbox); setSt(d); }).catch(() => {});
  useEffect(() => { refresh(); }, [tick]);
  useEffect(() => {
    if (!st?.running && st?.status !== "running") return undefined;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [st?.running, st?.status]); // eslint-disable-line
  const prevRunning = React.useRef(false);
  useEffect(() => {
    const running = !!st?.running;
    if (prevRunning.current && !running) setTick((x) => x + 1);
    prevRunning.current = running;
  }, [st?.running]);

  const scan = async () => {
    try { await mailApi.scan(); toast.success("Analisi avviata"); setTimeout(refresh, 500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!st) return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#4a1c17]" /></div>;
  const running = st.running || st.status === "running";
  const pct = running && st.total ? Math.round((st.done / st.total) * 100) : null;
  const counts = { reply: st.needs_reply, bulk: st.bulk, auto: st.auto, people: st.people, rules: st.rules, all: st.senders, labels: st.new_senders };

  return (
    <div data-testid="mail-cleanup-panel">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Posta — {st.mailbox || "non configurata"}</h2>
          <p className="mt-1 text-sm text-[#7c5a4e]">Metti in evidenza chi aspetta una risposta, archivia o cestina per mittente, blocca le pubblicità e disiscriviti.</p>
        </div>
        <button onClick={scan} disabled={running || !st.configured} className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="mail-scan-btn">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {running ? (pct !== null ? `Analisi ${pct}%` : "Analisi…") : st.indexed ? "Aggiorna" : "Analizza casella"}
        </button>
      </div>
      {running && <div className="mt-3 h-1.5 rounded-full bg-[#f0e6d8] overflow-hidden"><div className="h-full bg-[#C48B31] transition-all" style={{ width: `${pct ?? 5}%` }} /></div>}
      {st.status === "error" && <p className="mt-2 text-sm text-red-700" data-testid="mail-scan-error">Errore analisi: {st.error}</p>}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="mail-stats">
        {[["In arrivo", st.inbox_total ?? st.indexed], ["Non lette", st.unseen], ["Mittenti", st.senders], ["Pubblicità", st.bulk], ["Da rispondere", st.needs_reply]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-white border border-[#e8dcc8] px-3 py-2"><div className="text-[11px] uppercase tracking-wide text-[#7c5a4e]">{l}</div><div className="text-xl font-bold text-[#2b1410]">{v ?? 0}</div></div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-1.5 border-b border-[#e8dcc8] pb-2" data-testid="mail-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${tab === k ? "bg-[#4a1c17] text-[#f7ebdf]" : "bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#f7ebdf]"}`} data-testid={`mail-tab-${k}`}>
            <Icon className="h-3.5 w-3.5" /> {l}{counts[k] > 0 && <span className={`rounded-full px-1.5 text-[10px] font-bold ${tab === k ? "bg-white/20" : "bg-[#f7ebdf]"}`}>{counts[k]}</span>}
          </button>
        ))}
      </div>
      <div className="mt-4" key={`${tab}-${tick}`}>
        {tab === "guests" && <GuestRepliesPanel />}
        {tab === "reply" && <MailNeedsReply onChanged={refresh} />}
        {tab === "rules" && <MailRules onChanged={refresh} />}
        {tab === "labels" && <MailLabels onChanged={refresh} />}
        {["bulk", "auto", "people", "all"].includes(tab) && <MailSenders kind={tab} onChanged={refresh} />}
      </div>
    </div>
  );
}
